/**
 * Unified Recording Preview Modal
 * Supports both scene-based and single recording modes
 * Shows preview with save/delete options after recording stops
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { Modal, Button, Spin } from 'antd';
import { Clock, Film, MonitorPlay, AlertCircle, Video } from 'lucide-react';
import { 
  recordingPreviewAtom, 
  hideRecordingPreviewAtom,
  nextSceneAtom,
  activeProjectAtom,
  sceneListAtom,
  currentSceneAtom,
} from '../../atoms/sceneAtoms';
import { Scene, SceneStatus, SceneUploadStatus } from '../../../../types/scene';
import SceneService from '../../services/sceneService';
import { db, getPendingRecordingBySceneId, IPendingRecordingRow } from '../../idb/chunk-db';
import logger from '../../utils/logger';
import { s3KeyStorage } from '../../services/s3KeyStorage';
import { thumbnailStorage } from '../../services/thumbnailStorage';
import { uploadQueueManager } from '../../services/uploadQueueManager';
import { getUploadConfig } from '../../utils/authUtils';
import { generateRecordingFilename } from '../../utils/downloadChunkedBlob';
import UploadWorker from '../../worker/upload.worker';
import SceneAnalysis from './SceneAnalysis';
import SceneAnalysisService from '../../../../services/sceneAnalysisService';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useUpgradeModal } from '../../premiumFeatures/context/UpgradeModalContext';
import { useRecorder } from '../../context/RecorderContext';
import { RECORDING_FLAGS } from '../../constants/recordingFlags';
import { CompositorPlayer, CompositorPlayerRef } from '../../compositor';
import { CompositorRecordingData } from '../../compositor/types/manifest';
import { captureCompositorThumbnail, uploadCompositorThumbnail, blobToDataUrl } from '../../services/compositorThumbnailService';
import '../modals/ScenePreviewModal.scss';

interface RecordingPreviewModalProps {
  onDelete?: () => void;
  onReRecord?: (sceneId: string) => void;
  recoveredVideoUrl?: string | null;
  recoveredPendingData?: any;
  onClearRecoveredData?: () => void;
}

const RecordingPreviewModal: React.FC<RecordingPreviewModalProps> = ({
  onDelete,
  onReRecord,
  recoveredVideoUrl,
  recoveredPendingData,
  onClearRecoveredData,
}) => {
  const [searchParams] = useSearchParams();
  const recordingMode = searchParams.get("mode") || "project";
  
  const [previewState] = useAtom(recordingPreviewAtom);
  const [, hidePreview] = useAtom(hideRecordingPreviewAtom);
  const [, goToNextScene] = useAtom(nextSceneAtom);
  const [activeProject, setActiveProject] = useAtom(activeProjectAtom);
  const [sceneList, setSceneList] = useAtom(sceneListAtom);
  const [currentScene, setCurrentScene] = useAtom(currentSceneAtom);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAnalysisProcessing, setIsAnalysisProcessing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const compositorRef = useRef<CompositorPlayerRef>(null);
  
  // Phase 1.3: Deferred upload state
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadStatus, setUploadStatus] = useState<SceneUploadStatus>('not_started');
  const [uploadQueueId, setUploadQueueId] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  // Feature access and upgrade modal for duration limit enforcement
  const { getLimit, isPaid } = useFeatureAccess();
  const { showUpgradeModal } = useUpgradeModal();
  
  // Separate stream recording methods for scene mode deferred upload
  const { 
    separateStreamPendingRecording,
    uploadSeparateStreamRecording,
    uploadFromRecoveredData,
    clearSeparateStreamRecording,
  } = useRecorder();

  const { visible, mode, videoGuid, sceneId, scene, duration, s3Key, micEnabled } = previewState;
  
  // Don't show preview modal in blank recording mode - it only shows in project mode
  // This check must be after all hooks to comply with React rules of hooks
  const isBlankMode = recordingMode === 'blank';
  
  // Extract project ID from nested structure: { project: { id: ... }, scenes: [], transitions: [] }
  const actualProject = (activeProject as any)?.project || activeProject;
  const projectId = actualProject?.id;
  
  // State for IndexedDB-recovered pending recording (after page refresh)
  const [idbPendingRecording, setIdbPendingRecording] = useState<IPendingRecordingRow | null>(null);
  
  // Use either in-memory pending recording or IndexedDB-recovered pending recording
  const effectivePendingRecording = separateStreamPendingRecording || idbPendingRecording;
  
  // Load pending recording from IndexedDB when modal opens for pending_confirmation scene
  useEffect(() => {
    const loadPendingFromIndexedDB = async () => {
      // Only load if:
      // 1. Modal is visible
      // 2. Scene exists with status pending_confirmation  
      // 3. No in-memory pending recording exists
      // 4. We haven't already loaded from IndexedDB
      const activeScene = scene || currentScene;
      if (!visible || !activeScene || activeScene.status !== 'pending_confirmation') {
        return;
      }
      if (separateStreamPendingRecording) {
        // Already have in-memory recording, no need to load from IndexedDB
        return;
      }
      
      try {
        const pendingRow = await getPendingRecordingBySceneId(activeScene.id.toString());
        if (pendingRow) {
          logger.info('[RecordingPreviewModal] Loaded pending recording from IndexedDB', {
            sceneId: activeScene.id,
            hasWebcamSegments: pendingRow.webcamSegments?.length || 0,
            hasScreenSegments: pendingRow.screenSegments?.length || 0,
          });
          setIdbPendingRecording(pendingRow);
        } else {
          logger.info('[RecordingPreviewModal] No pending recording found in IndexedDB for scene', activeScene.id);
        }
      } catch (error) {
        logger.error('[RecordingPreviewModal] Failed to load pending recording from IndexedDB', error);
      }
    };
    
    loadPendingFromIndexedDB();
  }, [visible, scene, currentScene, separateStreamPendingRecording]);
  
  // Clear IndexedDB pending recording when modal closes
  useEffect(() => {
    if (!visible) {
      setIdbPendingRecording(null);
    }
  }, [visible]);
  
  // Compositor player integration for separate stream recordings
  // Check if saved scene is a separate streams recording with S3 URLs
  const savedSceneHasSeparateStreams = useMemo(() => {
    const activeScene = scene || currentScene;
    if (!activeScene?.metadata) return false;
    
    const metadata = typeof activeScene.metadata === 'string' 
      ? JSON.parse(activeScene.metadata) 
      : activeScene.metadata;
    
    if (metadata.recordingType !== 'separate_streams') return false;
    
    // Check for segments with S3 keys in streams
    const streams = metadata.streams;
    if (!streams) return false;
    
    const hasWebcamWithKeys = streams.webcam?.segments?.some((s: any) => s.key);
    const hasScreenWithKeys = streams.screen?.segments?.some((s: any) => s.key);
    const hasAudioWithKeys = streams.audio?.segments?.some((s: any) => s.key || s.hasMic);
    
    return hasWebcamWithKeys || hasScreenWithKeys || hasAudioWithKeys;
  }, [scene, currentScene]);
  
  const useCompositorForPreview = useMemo(() => {
    if (!RECORDING_FLAGS.USE_COMPOSITOR_PLAYER) return false;
    
    // First check: pending recording (live preview after recording)
    if (effectivePendingRecording) {
      const layout = effectivePendingRecording.layout;
      if (!layout) return false;
      
      const hasWebcamSegments = layout.webcam?.segments && layout.webcam.segments.length > 0;
      const hasScreenSegments = layout.screen?.segments && layout.screen.segments.length > 0;
      
      if (hasWebcamSegments || hasScreenSegments) return true;
      
      const hasAudioSegments = 
        (effectivePendingRecording as any).audioSegmentBlobUrls?.length > 0 ||
        (idbPendingRecording?.audioSegments?.length ?? 0) > 0;
      const hasBackground = !!layout.background;
      const hasMicEnabled = effectivePendingRecording.micEnabled;
      if (hasAudioSegments || (hasMicEnabled && hasBackground)) return true;
    }
    
    // Second check: saved scene with separate streams (preview from scene card)
    return savedSceneHasSeparateStreams;
  }, [effectivePendingRecording, idbPendingRecording, savedSceneHasSeparateStreams]);
  
  // Build compositor recording data from pending recording OR saved scene metadata
  const compositorRecordingData = useMemo((): CompositorRecordingData | null => {
    if (!useCompositorForPreview) return null;
    
    const activeScene = scene || currentScene;
    
    // Case 1: Build from pending recording (live preview after recording)
    if (effectivePendingRecording) {
      const layout = effectivePendingRecording.layout;
      if (!layout) return null;
      
      console.group('[RecordingPreviewModal] 🎨 PENDING RECORDING - Background Resolution');
      console.log('A. effectivePendingRecording.layout.background (RECORDED from canvas):', layout.background);
      console.log('B. activeScene?.background (template default):', activeScene?.background);
      
      // PRIORITY: layout.background (recorded from canvas) is the authoritative source
      // This is what the user actually had selected when they recorded
      // activeScene.background is just the template default and should NOT override the recorded state
      const effectiveBackground = layout.background;
      
      console.log('C. FINAL effectiveBackground (using recorded layout.background):', effectiveBackground);
      console.groupEnd();
      
      // Build audio segments - check if any audio segments exist (don't rely on boolean flags)
      let audioSegments: any[] | undefined;
      
      // Check in-memory pending recording audio segment blob URLs first
      const pendingAudioUrls = (effectivePendingRecording as any).audioSegmentBlobUrls;
      if (pendingAudioUrls?.length) {
        audioSegments = pendingAudioUrls.map(({ segmentIndex }: { segmentIndex: number }) => ({
          segmentIndex,
          tsStart: 0,
          tsEnd: effectivePendingRecording.duration,
          hasMic: true,
          hasSystemAudio: false,
          isMuted: false,
        }));
      }
      // Check IDB-recovered pending recording audio segments (has raw blobs)
      else if (idbPendingRecording?.audioSegments?.length) {
        audioSegments = idbPendingRecording.audioSegments.map((seg) => ({
          segmentIndex: seg.segmentIndex,
          tsStart: seg.tsStart,
          tsEnd: seg.tsEnd ?? effectivePendingRecording.duration,
          hasMic: seg.hasMic,
          hasSystemAudio: seg.hasSystemAudio,
          isMuted: seg.isMuted,
        }));
      }
      
      return {
        duration: effectivePendingRecording.duration,
        layout: {
          canvas: layout.canvas,
          webcam: layout.webcam,
          screen: layout.screen,
          background: effectiveBackground,
        },
        streams: {
          webcam: layout.webcam?.segments ? { segments: layout.webcam.segments } : undefined,
          screen: layout.screen?.segments ? { segments: layout.screen.segments } : undefined,
          audio: audioSegments ? { segments: audioSegments } : undefined,
        },
        version: '2.1',
      };
    }
    
    // Case 2: Build from saved scene metadata (preview from scene card)
    if (savedSceneHasSeparateStreams && activeScene?.metadata) {
      const metadata = typeof activeScene.metadata === 'string' 
        ? JSON.parse(activeScene.metadata) 
        : activeScene.metadata;
      
      const layout = metadata.layout;
      const streams = metadata.streams;
      if (!layout || !streams) return null;
      
      const effectiveBackground = layout.background || { type: 'color' as const, value: '#1a1a2e' };
      
      return {
        duration: metadata.duration || activeScene.duration || 0,
        layout: {
          canvas: layout.canvas,
          webcam: layout.webcam,
          screen: layout.screen,
          background: effectiveBackground,
        },
        streams: {
          webcam: streams.webcam?.segments ? { segments: streams.webcam.segments } : undefined,
          screen: streams.screen?.segments ? { segments: streams.screen.segments } : undefined,
          audio: streams.audio?.segments ? { segments: streams.audio.segments } : undefined,
        },
        version: '2.1',
      };
    }
    
    return null;
  }, [useCompositorForPreview, effectivePendingRecording, idbPendingRecording, scene, currentScene, savedSceneHasSeparateStreams]);
  
  // Build pending blob URLs map from pending recording or recovered data
  const compositorBlobUrls = useMemo((): Map<string, string> => {
    const blobUrlsMap = new Map<string, string>();
    
    if (!useCompositorForPreview) return blobUrlsMap;
    
    // If we have recovered data with per-segment blobs, use those
    if (recoveredPendingData) {
      const webcamSegments = recoveredPendingData.webcamSegments || [];
      webcamSegments.forEach((segment: any, index: number) => {
        if (segment.blob) {
          const key = `webcam-${segment.segmentIndex ?? index}`;
          blobUrlsMap.set(key, URL.createObjectURL(segment.blob));
        }
      });
      
      const screenSegments = recoveredPendingData.screenSegments || [];
      screenSegments.forEach((segment: any, index: number) => {
        if (segment.blob) {
          const key = `screen-${segment.segmentIndex ?? index}`;
          blobUrlsMap.set(key, URL.createObjectURL(segment.blob));
        }
      });
      
      // Add audio segments from recovered data
      const audioSegments = recoveredPendingData.audioSegments || [];
      audioSegments.forEach((segment: any, index: number) => {
        if (segment.blob) {
          const key = `audio-${segment.segmentIndex ?? index}`;
          blobUrlsMap.set(key, URL.createObjectURL(segment.blob));
        }
      });
      
      return blobUrlsMap;
    }
    
    // Handle IndexedDB-recovered pending recording (has blobs directly, not blob URLs)
    if (idbPendingRecording) {
      // Create blob URLs from stored blobs
      if (idbPendingRecording.webcamSegments) {
        idbPendingRecording.webcamSegments.forEach((segment) => {
          if (segment.blob) {
            const key = `webcam-${segment.segmentIndex}`;
            const blobUrl = URL.createObjectURL(segment.blob);
            blobUrlsMap.set(key, blobUrl);
          }
        });
      }
      
      if (idbPendingRecording.screenSegments) {
        idbPendingRecording.screenSegments.forEach((segment) => {
          if (segment.blob) {
            const key = `screen-${segment.segmentIndex}`;
            const blobUrl = URL.createObjectURL(segment.blob);
            blobUrlsMap.set(key, blobUrl);
          }
        });
      }
      
      // Add audio segments from IndexedDB recovered data
      if (idbPendingRecording.audioSegments) {
        idbPendingRecording.audioSegments.forEach((segment) => {
          if (segment.blob) {
            const key = `audio-${segment.segmentIndex}`;
            const blobUrl = URL.createObjectURL(segment.blob);
            blobUrlsMap.set(key, blobUrl);
          }
        });
      }
      
      return blobUrlsMap;
    }
    
    // Otherwise use in-memory pending recording blob URLs
    if (!separateStreamPendingRecording) return blobUrlsMap;
    
    const layout = separateStreamPendingRecording.layout;
    if (!layout) return blobUrlsMap;
    
    // Use per-segment blob URLs if available (multi-segment recordings)
    if (separateStreamPendingRecording.webcamSegmentBlobUrls?.length) {
      separateStreamPendingRecording.webcamSegmentBlobUrls.forEach(({ segmentIndex, blobUrl }) => {
        const key = `webcam-${segmentIndex}`;
        blobUrlsMap.set(key, blobUrl);
        console.log('[RecordingPreviewModal] Set webcam segment blob URL:', key, blobUrl.substring(0, 50));
      });
    } else if (layout.webcam?.segments) {
      // Fallback to single blob URL for legacy single-segment recordings
      layout.webcam.segments.forEach((segment: any, index: number) => {
        const key = `webcam-${segment.segmentIndex ?? index}`;
        if (separateStreamPendingRecording.webcamBlobUrl) {
          blobUrlsMap.set(key, separateStreamPendingRecording.webcamBlobUrl);
        }
      });
    }
    
    if (separateStreamPendingRecording.screenSegmentBlobUrls?.length) {
      separateStreamPendingRecording.screenSegmentBlobUrls.forEach(({ segmentIndex, blobUrl }) => {
        const key = `screen-${segmentIndex}`;
        blobUrlsMap.set(key, blobUrl);
        console.log('[RecordingPreviewModal] Set screen segment blob URL:', key, blobUrl.substring(0, 50));
      });
    } else if (layout.screen?.segments) {
      // Fallback to single blob URL for legacy single-segment recordings
      layout.screen.segments.forEach((segment: any, index: number) => {
        const key = `screen-${segment.segmentIndex ?? index}`;
        if (separateStreamPendingRecording.screenBlobUrl) {
          blobUrlsMap.set(key, separateStreamPendingRecording.screenBlobUrl);
        }
      });
    }
    
    // Add audio segment blob URLs for dedicated audio stream
    if (separateStreamPendingRecording.audioSegmentBlobUrls?.length) {
      separateStreamPendingRecording.audioSegmentBlobUrls.forEach(({ segmentIndex, blobUrl }) => {
        const key = `audio-${segmentIndex}`;
        blobUrlsMap.set(key, blobUrl);
        console.log('[RecordingPreviewModal] Set audio segment blob URL:', key, blobUrl.substring(0, 50));
      });
    }
    
    return blobUrlsMap;
  }, [useCompositorForPreview, separateStreamPendingRecording, idbPendingRecording, recoveredPendingData]);
  
  // Save micEnabled to scene for persistence across refreshes
  React.useEffect(() => {
    if (visible && scene && sceneId) {
      const finalMicEnabled = micEnabled ?? scene.micEnabled ?? false;
      
      // Save micEnabled to scene in IndexedDB if not already set
      if (micEnabled !== undefined && scene.micEnabled !== micEnabled) {
        (async () => {
          try {
            await SceneService.updateScene(sceneId, { micEnabled: finalMicEnabled }, projectId);
          } catch (err) {
            console.error('[RecordingPreviewModal] Failed to save micEnabled to scene:', err);
          }
        })();
      }
    }
  }, [visible, scene, sceneId, micEnabled, projectId]);
  
  useEffect(() => {
    
    // Phase 1.2: Deferred Upload - Immediate enable for scene mode
    if (mode === 'scene') {
      // Scene mode: Upload is deferred, enable buttons immediately
      setUploadComplete(true);
    } else {
      // Single mode: Keep legacy polling behavior
      if (videoGuid) {
        const hasS3Key = s3Key || s3KeyStorage.get(videoGuid);
        setUploadComplete(!!hasS3Key);
      }
    }
  }, [visible, mode, videoGuid, sceneId, scene, duration, s3Key]);

  // Phase 1.2: Poll for upload completion ONLY in single mode (legacy behavior)
  // In scene mode, upload is deferred until user clicks "Looks Good"
  useEffect(() => {
    if (!visible || !videoGuid || uploadComplete || mode === 'scene') {
      return;
    }

    let pollCount = 0;
    const MAX_POLLS = 30; // 30 * 500ms = 15 seconds (enough time for uploads)

    const pollInterval = setInterval(() => {
      pollCount++;
      const hasS3Key = s3KeyStorage.get(videoGuid);
      if (hasS3Key) {
        setUploadComplete(true);
        clearInterval(pollInterval);
      } else if (pollCount >= MAX_POLLS) {
        setUploadComplete(true); // Allow user to proceed anyway
        clearInterval(pollInterval);
      }
    }, 500);

    return () => clearInterval(pollInterval);
  }, [visible, videoGuid, uploadComplete, mode]);

  // Helper to get video blob by videoGuid for single mode
  const getVideoBlobByGuid = async (guid: string): Promise<Blob | null> => {
    try {
      const chunks = await db.chunks
        .where('videoGuid')
        .equals(guid)
        .sortBy('chunkNo');

      console.log(`🔍 [DEBUG-PREVIEW] getVideoBlobByGuid(${guid}): found ${chunks.length} chunks`, chunks.map(c => ({ id: c.id, chunkNo: c.chunkNo, size: c.blob?.size ?? 0, isLast: c.isLast, status: c.status })));

      if (chunks.length === 0) {
        return null;
      }

      const blobs = chunks.map(chunk => chunk.blob);
      const totalSize = blobs.reduce((sum, b) => sum + (b?.size ?? 0), 0);
      const combined = new Blob(blobs, { type: 'video/webm' });
      console.log(`🔍 [DEBUG-PREVIEW] Combined blob: size=${combined.size}, individualSizes=${blobs.map(b => b?.size ?? 0).join(',')}, totalSize=${totalSize}`);
      return combined;
    } catch (error) {
      console.error(`🔍 [DEBUG-PREVIEW] getVideoBlobByGuid ERROR:`, error);
      logger.error('[RecordingPreviewModal] Failed to get video blob:', error);
      return null;
    }
  };

  useEffect(() => {
    console.log(`🔍 [DEBUG-PREVIEW] useEffect triggered:`, {
      visible,
      videoGuid,
      s3Key: !!s3Key,
      mode,
      sceneId,
      useCompositorForPreview,
      hasEffectivePendingRecording: !!effectivePendingRecording,
      hasSeparateStreamPendingRecording: !!separateStreamPendingRecording,
      recoveredVideoUrl: !!recoveredVideoUrl,
      savedSceneHasSeparateStreams,
      sceneStatus: scene?.status,
    });

    if (visible && useCompositorForPreview && effectivePendingRecording) {
      console.log(`🔍 [DEBUG-PREVIEW] Taking PATH: compositor + effectivePendingRecording`);
      logger.info('[RecordingPreviewModal] Using compositor for separate stream recording preview', {
        duration: effectivePendingRecording.duration,
        fromIndexedDB: !!idbPendingRecording,
      });
      setVideoDuration(effectivePendingRecording.duration / 1000);
      setLoading(false);
      setUploadComplete(true);
      return;
    }
    
    if (visible && useCompositorForPreview && savedSceneHasSeparateStreams && compositorRecordingData) {
      console.log(`🔍 [DEBUG-PREVIEW] Taking PATH: compositor + saved separate streams`);
      logger.info('[RecordingPreviewModal] Using compositor for saved separate stream recording', {
        duration: compositorRecordingData.duration,
      });
      setVideoDuration(compositorRecordingData.duration / 1000);
      setLoading(false);
      setUploadComplete(true);
      return;
    }
    
    if (visible && separateStreamPendingRecording?.combinedBlobUrl) {
      console.log(`🔍 [DEBUG-PREVIEW] Taking PATH: separateStreamPendingRecording with combinedBlobUrl`);
      logger.info('[RecordingPreviewModal] Loading separate stream recording preview (non-compositor)', {
        hasScreenBlobUrl: !!separateStreamPendingRecording.screenBlobUrl,
        hasWebcamBlobUrl: !!separateStreamPendingRecording.webcamBlobUrl,
        duration: separateStreamPendingRecording.duration,
      });
      setVideoUrl(separateStreamPendingRecording.combinedBlobUrl);
      setVideoDuration(separateStreamPendingRecording.duration / 1000);
      setLoading(false);
      setUploadComplete(true);
      return;
    }
    
    if (visible && recoveredVideoUrl && !separateStreamPendingRecording?.combinedBlobUrl && !useCompositorForPreview) {
      console.log(`🔍 [DEBUG-PREVIEW] Taking PATH: recovered recording from IndexedDB`);
      logger.info('[RecordingPreviewModal] Loading recovered recording from IndexedDB', {
        recoveredVideoUrl: recoveredVideoUrl.substring(0, 50) + '...',
        duration,
      });
      setVideoUrl(recoveredVideoUrl);
      setVideoDuration((duration || 0) / 1000);
      setLoading(false);
      setUploadComplete(true);
      return;
    }
    
    if (!visible || (!videoGuid && !s3Key)) {
      console.log(`🔍 [DEBUG-PREVIEW] Taking PATH: early exit (visible=${visible}, videoGuid=${videoGuid}, s3Key=${!!s3Key})`);
      return;
    }

    console.log(`🔍 [DEBUG-PREVIEW] Taking PATH: loadVideo from IndexedDB chunks (videoGuid=${videoGuid})`);

    const loadVideo = async () => {
      setLoading(true);
      setError(null);

      try {
        if (s3Key && scene?.status === 'pending_confirmation') {
          console.log(`🔍 [DEBUG-PREVIEW] Loading from S3 URL`);
          setVideoUrl(s3Key);
          setLoading(false);
          setUploadComplete(true);
          logger.info('[RecordingPreviewModal] Video loaded from S3 URL', { mode, sceneId, s3Key });
          return;
        }

        if (!videoGuid) {
          console.error(`🔍 [DEBUG-PREVIEW] No videoGuid - setting error`);
          setError('No video data available');
          setLoading(false);
          return;
        }

        let blob: Blob | null = null;
        let retryCount = 0;
        const MAX_RETRIES = 10;

        console.log(`🔍 [DEBUG-PREVIEW] Starting retry loop for videoGuid=${videoGuid}, maxRetries=${MAX_RETRIES}`);
        while (retryCount < MAX_RETRIES && !blob) {
          blob = await getVideoBlobByGuid(videoGuid);
          
          if (!blob) {
            console.log(`🔍 [DEBUG-PREVIEW] Retry ${retryCount + 1}/${MAX_RETRIES}: no chunks found, waiting 200ms...`);
            await new Promise(resolve => setTimeout(resolve, 200));
            retryCount++;
          }
        }
        
        if (!blob) {
          console.error(`🔍 [DEBUG-PREVIEW] ALL ${MAX_RETRIES} RETRIES EXHAUSTED - no chunks found for videoGuid=${videoGuid}`);
          const allChunks = await db.chunks.toArray();
          console.log(`🔍 [DEBUG-PREVIEW] Total chunks in IDB: ${allChunks.length}`, allChunks.map(c => ({ videoGuid: c.videoGuid, chunkNo: c.chunkNo, size: c.blob?.size ?? 0 })));
          setError('No video data available');
          setLoading(false);
          return;
        }

        console.log(`🔍 [DEBUG-PREVIEW] Video blob obtained: size=${blob.size}, type=${blob.type}, retries=${retryCount}`);
        const url = URL.createObjectURL(blob);
        setVideoUrl(url);
        setLoading(false);
        
        logger.info('[RecordingPreviewModal] Video loaded successfully', { mode, videoGuid });
      } catch (err) {
        console.error(`🔍 [DEBUG-PREVIEW] loadVideo EXCEPTION:`, err);
        logger.error('[RecordingPreviewModal] Failed to load video:', err);
        setError('Failed to load video. Please try again.');
        setLoading(false);
      }
    };

    loadVideo();

    return () => {
      // Only revoke blob URLs, not S3 URLs
      if (videoUrl && videoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(videoUrl);
        setVideoUrl(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, mode, videoGuid, sceneId, s3Key, scene?.status, recoveredVideoUrl, useCompositorForPreview, savedSceneHasSeparateStreams, compositorRecordingData?.duration]);

  // Refs to hold values needed for afterClose cleanup (since state may be stale)
  const videoUrlRef = useRef<string | null>(null);
  const videoGuidRef = useRef<string | null>(null);
  
  // Keep refs updated
  useEffect(() => {
    videoUrlRef.current = videoUrl;
    videoGuidRef.current = videoGuid || null;
  }, [videoUrl, videoGuid]);
  
  const handleClose = useCallback(async () => {
    // Pause video immediately to stop playback
    if (compositorRef.current) {
      compositorRef.current.pause();
    } else if (videoRef.current) {
      videoRef.current.pause();
    }
    
    // If closing without confirming in scene mode, set status to pending_upload
    // so the user can come back and confirm later
    // NOTE: Only update sceneList, not currentScene, to avoid triggering layout reapply effects
    if (mode === 'scene' && sceneId && videoGuid) {
      try {
        // Check if there are chunks for this video
        const chunks = await db.chunks
          .where('videoGuid')
          .equals(videoGuid)
          .count();
        
        if (chunks > 0) {
          logger.info('[RecordingPreviewModal] Closing without confirm - setting status to pending_upload', { sceneId, videoGuid });
          
          // Update scene status to pending_upload in local state only
          // Don't update currentScene to avoid triggering canvas layout effects
          const updatedSceneList = sceneList.map(s => 
            s.id === Number(sceneId) 
              ? { ...s, status: 'pending_upload' as SceneStatus }
              : s
          );
          setSceneList(updatedSceneList);
        }
      } catch (err) {
        logger.error('[RecordingPreviewModal] Failed to update scene status on close:', err);
      }
    }
    
    // Hide the modal - state reset happens in afterClose
    hidePreview();
  }, [videoGuid, hidePreview, mode, sceneId, sceneList, setSceneList]);
  
  // Reset state after modal animation completes
  const handleAfterClose = useCallback(() => {
    // Reset video/compositor element
    if (compositorRef.current) {
      compositorRef.current.seekToSeconds(0);
    } else if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
    
    // Revoke blob URL
    if (videoUrlRef.current && videoUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(videoUrlRef.current);
    }
    
    // Clean up S3 key from storage
    if (videoGuidRef.current) {
      s3KeyStorage.delete(videoGuidRef.current);
    }
    
    // Reset all state
    setVideoUrl(null);
    setError(null);
    setCurrentTime(0);
    setIsPlaying(false);
    setUploadProgress(0);
    setUploadStatus('not_started');
    setUploadQueueId(null);
    setRetryCount(0);
    setIsConfirming(false);
  }, []);

  /**
   * Phase 1.3: Trigger deferred upload for scene mode
   * This function starts the S3 multipart upload process (single attempt)
   */
  const triggerDeferredUploadAttempt = useCallback(async (
    videoGuidToUpload: string,
    sceneIdToUpload: string,
    projectIdToUpload: string | number,
    micEnabledParam?: boolean,
    isTempParam?: boolean
  ): Promise<{ success: boolean; error?: string; fileUrl?: string; thumbnailUrl?: string }> => {
    try {
      logger.info('[RecordingPreviewModal] Starting deferred upload:', {
        videoGuid: videoGuidToUpload,
        sceneId: sceneIdToUpload,
        projectId: projectIdToUpload,
      });

      // Get all chunks for this videoGuid
      console.log('[triggerDeferredUploadAttempt] Querying chunks for videoGuid:', videoGuidToUpload);
      const chunks = await db.chunks
        .where('videoGuid')
        .equals(videoGuidToUpload)
        .sortBy('chunkNo');

      console.log('[triggerDeferredUploadAttempt] Found chunks:', chunks.length);
      if (chunks.length === 0) {
        console.log('[triggerDeferredUploadAttempt] No chunks found, throwing error');
        throw new Error('No chunks found for upload');
      }

      // Find the true last chunk - the first chunk with isLast: true
      // Any chunks after this are stale data that should be excluded
      const lastChunkIndex = chunks.findIndex(c => c.isLast === true);
      let chunksToUpload = chunks;
      
      if (lastChunkIndex !== -1 && lastChunkIndex < chunks.length - 1) {
        // There are chunks after the isLast chunk - these are stale and should be excluded
        chunksToUpload = chunks.slice(0, lastChunkIndex + 1);
        console.log('[triggerDeferredUploadAttempt] Excluding stale chunks after isLast:', {
          originalCount: chunks.length,
          filteredCount: chunksToUpload.length,
          lastChunkNo: chunksToUpload[chunksToUpload.length - 1]?.chunkNo,
          excludedChunkNos: chunks.slice(lastChunkIndex + 1).map(c => c.chunkNo)
        });
      } else {
        console.log('[triggerDeferredUploadAttempt] No stale chunks to exclude, using all chunks');
      }

      logger.info(`[RecordingPreviewModal] Found ${chunksToUpload.length} chunks to upload (filtered from ${chunks.length})`);

      // Add to upload queue
      console.log('[triggerDeferredUploadAttempt] Adding to upload queue...');
      const queueId = await uploadQueueManager.addToQueue(
        sceneIdToUpload,
        videoGuidToUpload,
        String(projectIdToUpload),
        chunksToUpload.length
      );
      console.log('[triggerDeferredUploadAttempt] Queue ID:', queueId);
      setUploadQueueId(queueId);

      // Create upload worker
      console.log('[triggerDeferredUploadAttempt] Creating upload worker...');
      const uploadWorker = new UploadWorker();
      console.log('[triggerDeferredUploadAttempt] Getting upload config...');
      const uploadConfig = getUploadConfig();
      console.log('[triggerDeferredUploadAttempt] Upload config:', { hasAuthToken: !!uploadConfig.authToken, hasUserId: !!uploadConfig.userId });

      // Validate auth config before upload
      if (!uploadConfig.authToken || !uploadConfig.userId) {
        const missingParams = [];
        if (!uploadConfig.authToken) missingParams.push('authentication token');
        if (!uploadConfig.userId) missingParams.push('user ID');
        throw new Error(`Missing required parameters: ${missingParams.join(', ')}. Please sign in and try again.`);
      }
      console.log('[triggerDeferredUploadAttempt] Auth validation passed');

      // Set upload status to uploading (ONLY if not a temporary analysis upload)
      if (!isTempParam) {
        setUploadStatus('uploading');
        setUploadProgress(0);
      }
      console.log('[triggerDeferredUploadAttempt] Creating Promise...');

      return new Promise((resolve, reject) => {
        console.log('[triggerDeferredUploadAttempt] Inside Promise, setting up timeout...');
        // Add timeout to prevent hanging forever (2 minutes should be enough for most uploads)
        const uploadTimeout = setTimeout(() => {
          logger.error('[RecordingPreviewModal] Upload timed out after 2 minutes');
          uploadWorker.terminate();
          reject(new Error('Upload timed out. Please try again.'));
        }, 120000);
        
        // Handle worker errors (worker crashed or message channel closed)
        uploadWorker.onerror = (errorEvent) => {
          clearTimeout(uploadTimeout);
          logger.error('[RecordingPreviewModal] Upload worker error:', errorEvent);
          uploadWorker.terminate();
          reject(new Error('Upload worker failed. Please try again.'));
        };
        
        // Handle worker messages
        uploadWorker.onmessage = async (event) => {
          console.log('[MainThread] Worker message received:', event.data?.type);
          const { type, progress, uploadedChunks, totalChunks, error, fileUrl, thumbnailUrl } = event.data;

          console.log('[MainThread] Parsed message:', { type, progress, uploadedChunks, totalChunks, hasError: !!error, hasFileUrl: !!fileUrl });
          logger.info(`[RecordingPreviewModal] Upload worker message:`, { type, progress, uploadedChunks, totalChunks });

          switch (type) {
            case 'upload-init-complete':
              console.log('[MainThread] Handling upload-init-complete, sending chunks...', { chunksCount: chunksToUpload.length });
              logger.info('[RecordingPreviewModal] Upload initialized, sending chunks...');
              
              // Send all chunks to upload - convert to ArrayBuffer in main thread for reliability
              (async () => {
                for (let index = 0; index < chunksToUpload.length; index++) {
                  const chunk = chunksToUpload[index];
                  console.log(`[MainThread] Processing chunk ${index + 1}/${chunksToUpload.length}, chunkNo: ${chunk.chunkNo}`);
                  
                  // Convert blob to ArrayBuffer in main thread (more reliable than in worker)
                  let chunkArrayBuffer: ArrayBuffer;
                  try {
                    if (chunk.blob instanceof Blob) {
                      chunkArrayBuffer = await chunk.blob.arrayBuffer();
                      console.log(`[MainThread] Converted Blob to ArrayBuffer for chunk ${chunk.chunkNo}`);
                    } else if (chunk.blob instanceof ArrayBuffer) {
                      chunkArrayBuffer = chunk.blob;
                      console.log(`[MainThread] Chunk ${chunk.chunkNo} already ArrayBuffer`);
                    } else if (chunk.blob && typeof chunk.blob === 'object') {
                      // Handle case where blob might be stored as ArrayBuffer but not recognized
                      chunkArrayBuffer = chunk.blob as ArrayBuffer;
                      console.log(`[MainThread] Chunk ${chunk.chunkNo} cast to ArrayBuffer`);
                    } else {
                      console.error(`[MainThread] Invalid chunk data type for chunk ${chunk.chunkNo}:`, typeof chunk.blob);
                      logger.error(`Invalid chunk data type for chunk ${chunk.chunkNo}`);
                      continue;
                    }
                  } catch (conversionError) {
                    console.error(`[MainThread] Failed to convert chunk ${chunk.chunkNo}:`, conversionError);
                    logger.error(`Failed to convert chunk ${chunk.chunkNo} to ArrayBuffer:`, conversionError);
                    continue;
                  }
                  
                  const chunkMessage = {
                    type: 'upload-chunk',
                    videoGuid: videoGuidToUpload,
                    chunkNo: chunk.chunkNo,
                    chunkData: chunkArrayBuffer,
                    isLast: index === chunksToUpload.length - 1, // Use filtered array length
                    authToken: uploadConfig.authToken, // Required by worker
                  };
                  
                  console.log(`[MainThread] Posting chunk ${chunk.chunkNo} to worker, isLast: ${chunkMessage.isLast}`);
                  // Transfer the ArrayBuffer to avoid copying (more efficient)
                  uploadWorker.postMessage(chunkMessage, [chunkArrayBuffer]);
                }
                console.log('[MainThread] All chunks sent to worker');
              })();
              break;

            case 'upload-progress':
              const progressPercent = Math.round((uploadedChunks / totalChunks) * 100);
              
              // Skip UI updates for temporary analysis uploads
              if (!isTempParam) {
                setUploadProgress(progressPercent);
              }
              
              if (queueId) {
                uploadQueueManager.updateProgress(queueId, uploadedChunks, totalChunks);
              }
              break;

            case 'upload-complete':
              clearTimeout(uploadTimeout);
              logger.info('[RecordingPreviewModal] Upload complete!', {
                fileUrl,
                thumbnailUrl,
              });

              // Skip UI updates for temporary analysis uploads
              if (!isTempParam) {
                setUploadProgress(100);
                setUploadStatus('upload_success');
              }

              if (queueId) {
                uploadQueueManager.markSuccess(queueId);
              }

              // Store S3 key and thumbnail
              if (fileUrl) {
                const s3Key = fileUrl.split('.amazonaws.com/')[1];
                if (s3Key) {
                  s3KeyStorage.set(videoGuidToUpload, s3Key);
                }
              }
              if (thumbnailUrl) {
                thumbnailStorage.set(videoGuidToUpload, thumbnailUrl);
              }

              uploadWorker.terminate();
              resolve({ success: true, fileUrl, thumbnailUrl });
              break;

            case 'upload-error':
            case 'chunk-error':
              clearTimeout(uploadTimeout);
              logger.error('[RecordingPreviewModal] Upload error:', error);
              setUploadStatus('upload_failed');

              if (queueId) {
                uploadQueueManager.markFailed(queueId, error || 'Upload failed');
              }

              uploadWorker.terminate();
              reject(new Error(error || 'Upload failed'));
              break;
          }
        };

        // Initialize upload
        console.log('[triggerDeferredUploadAttempt] Preparing init message...');
        const isCloudProject = !isNaN(Number(projectIdToUpload));
        // Use mp4 for blank mode (single recording), webm for cloud projects (scenes)
        const fileExtension = isCloudProject ? 'webm' : 'mp4';
        // Blank mode uses Dadan API via proxy, cloud projects use local S3 backend
        
        const initMessage = {
          type: 'init-upload',
          videoGuid: videoGuidToUpload,
          fileName: generateRecordingFilename(videoGuidToUpload, fileExtension),
          authToken: uploadConfig.authToken,
          userId: uploadConfig.userId,
          totalChunks: chunks.length,
          projectId: isCloudProject ? Number(projectIdToUpload) : undefined,
          sceneId: sceneIdToUpload,
          micEnabled: micEnabledParam ?? false,
          isTemp: isTempParam ?? false,
          // Blank mode: use Dadan API for uploads (via proxy), cloud projects: use local S3 backend
          useDadanApi: !isCloudProject,
        };
        
        console.log('[triggerDeferredUploadAttempt] Posting init message to worker...');
        uploadWorker.postMessage(initMessage);
        console.log('[triggerDeferredUploadAttempt] Init message posted, starting queue manager...');

        // Start upload in queue manager
        uploadQueueManager.startUpload(queueId, uploadWorker);
        console.log('[triggerDeferredUploadAttempt] Queue manager started, waiting for worker messages...');
      });
    } catch (error) {
      console.log('[triggerDeferredUploadAttempt] Caught error:', error instanceof Error ? error.message : error);
      logger.error('[RecordingPreviewModal] Deferred upload attempt failed:', error);
      setUploadStatus('upload_failed');
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Upload failed' 
      };
    }
  }, []);

  /**
   * Phase 3.1: Auto-retry wrapper with exponential backoff
   * Retries upload up to 10 times with increasing delays
   */
  const triggerDeferredUpload = useCallback(async (
    videoGuidToUpload: string,
    sceneIdToUpload: string,
    projectIdToUpload: string | number,
    maxRetries: number = 10
  ): Promise<{ success: boolean; error?: string; fileUrl?: string; thumbnailUrl?: string }> => {
    let attempt = 0;
    
    while (attempt < maxRetries) {
      try {
        logger.info(`[RecordingPreviewModal] Upload attempt ${attempt + 1}/${maxRetries}`);
        setRetryCount(attempt);

        const result = await triggerDeferredUploadAttempt(
          videoGuidToUpload,
          sceneIdToUpload,
          projectIdToUpload,
          micEnabled // Pass micEnabled from preview state
        );

        if (result.success) {
          logger.info('[RecordingPreviewModal] Upload successful!', {
            hasFileUrl: !!result.fileUrl,
            hasThumbnailUrl: !!result.thumbnailUrl,
          });
          setRetryCount(0); // Reset retry count on success
          return { 
            success: true, 
            fileUrl: result.fileUrl, 
            thumbnailUrl: result.thumbnailUrl 
          };
        }

        // Check for non-retryable errors - fail immediately without retrying
        const nonRetryableErrors = [
          'No chunks found for upload',
          'Missing required parameters',
          'Please sign in'
        ];
        const isNonRetryable = nonRetryableErrors.some(errMsg => 
          result.error?.includes(errMsg)
        );
        
        if (isNonRetryable) {
          logger.error('[RecordingPreviewModal] Non-retryable upload error:', result.error);
          setUploadStatus('upload_failed');
          return { 
            success: false, 
            error: result.error 
          };
        }

        // If upload failed, prepare for retry
        attempt++;
        
        if (attempt < maxRetries) {
          // Exponential backoff: 2^attempt seconds (1s, 2s, 4s, 8s, 16s, 32s, ...)
          const delayMs = Math.min(Math.pow(2, attempt) * 1000, 30000); // Max 30s delay
          logger.warn(`[RecordingPreviewModal] Upload attempt ${attempt} failed. Retrying in ${delayMs / 1000}s...`, {
            error: result.error,
            nextAttempt: attempt + 1,
            maxRetries
          });

          // Update status to show we're retrying
          setUploadStatus('retrying');
          
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, delayMs));
        } else {
          // All retries exhausted
          logger.error(`[RecordingPreviewModal] Upload failed after ${maxRetries} attempts`, {
            lastError: result.error
          });
          setUploadStatus('upload_failed');
          setRetryCount(attempt);
          return { 
            success: false, 
            error: result.error || `Upload failed after ${maxRetries} attempts` 
          };
        }
      } catch (error) {
        logger.error(`[RecordingPreviewModal] Upload attempt ${attempt + 1} threw error:`, error);
        attempt++;
        
        if (attempt < maxRetries) {
          const delayMs = Math.min(Math.pow(2, attempt) * 1000, 30000);
          logger.warn(`[RecordingPreviewModal] Retrying in ${delayMs / 1000}s...`);
          setUploadStatus('retrying');
          await new Promise(resolve => setTimeout(resolve, delayMs));
        } else {
          setUploadStatus('upload_failed');
          setRetryCount(attempt);
          return { 
            success: false, 
            error: error instanceof Error ? error.message : 'Upload failed' 
          };
        }
      }
    }

    // Fallback (should never reach here)
    return { success: false, error: 'Upload failed after all retries' };
  }, [triggerDeferredUploadAttempt]);

  const handleAcceptScene = useCallback(async () => {
    // Prevent double-clicks
    if (isConfirming) {
      return;
    }
    setIsConfirming(true);
    
    // Handle recovered separate stream recordings (from IndexedDB after navigation)
    if (recoveredPendingData) {
      logger.info('[RecordingPreviewModal] Uploading recovered separate stream recording', {
        sceneId: recoveredPendingData.sceneId,
        duration: recoveredPendingData.duration,
      });
      
      try {
        setUploadStatus('uploading');
        
        // THUMBNAIL: Capture BEFORE upload/status change while compositor is still mounted
        let capturedThumbnailDataUrl: string | null = null;
        const recoveredSceneId = recoveredPendingData.sceneId;
        if (useCompositorForPreview && compositorRef.current && projectId && recoveredSceneId) {
          const container = compositorRef.current.getContainerNode();
          if (container) {
            try {
              const blob = await captureCompositorThumbnail(container);
              if (blob) {
                capturedThumbnailDataUrl = await blobToDataUrl(blob);
                uploadCompositorThumbnail(blob, projectId, recoveredSceneId).catch(() => {});
              }
            } catch (err) {
              logger.warn('[RecordingPreviewModal] Thumbnail capture failed (non-blocking):', err);
            }
          }
        }
        
        await uploadFromRecoveredData(recoveredPendingData);
        setUploadStatus('upload_success');
        
        if (recoveredSceneId) {
          let freshMetadata: any = null;
          if (isCloudProject && projectId) {
            try {
              const { projectsManager } = await import('../../services/projectsManager');
              const freshProjectData: any = await projectsManager.getProject(Number(projectId));
              const freshScene = freshProjectData.scenes?.find((s: any) => s.id === Number(recoveredSceneId));
              if (freshScene) freshMetadata = freshScene.metadata;
            } catch {}
          }
          setSceneList((prevList) => {
            const updatedList = prevList.map((s) =>
              String(s.id) === String(recoveredSceneId)
                ? { 
                    ...s, 
                    status: 'recorded' as const, 
                    duration: recoveredPendingData.duration,
                    recordedAt: Date.now(),
                    ...(capturedThumbnailDataUrl ? { thumbnail: capturedThumbnailDataUrl } : {}),
                    ...(freshMetadata ? { metadata: freshMetadata } : {}),
                  }
                : s
            );
            const updatedCurrentScene = updatedList.find(s => String(s.id) === String(recoveredSceneId));
            if (updatedCurrentScene && String(currentScene?.id) === String(recoveredSceneId)) {
              setCurrentScene(updatedCurrentScene);
            }
            return updatedList;
          });
          logger.info('[RecordingPreviewModal] Updated local scene list status to recorded', { sceneId: recoveredSceneId, hasThumbnail: !!capturedThumbnailDataUrl });
        }
        
        onClearRecoveredData?.();
        hidePreview();
        
        logger.info('[RecordingPreviewModal] Recovered separate stream recording uploaded successfully');
      } catch (error) {
        logger.error('[RecordingPreviewModal] Recovered stream upload failed', error);
        setUploadStatus('upload_failed');
        setIsConfirming(false);
      }
      return;
    }
    
    // Handle IndexedDB pending recording (after page refresh)
    if (idbPendingRecording) {
      logger.info('[RecordingPreviewModal] Uploading IndexedDB pending recording', {
        sceneId: idbPendingRecording.sceneId,
        duration: idbPendingRecording.duration,
      });
      
      try {
        setUploadStatus('uploading');
        
        // THUMBNAIL: Capture BEFORE upload/status change while compositor is still mounted
        let capturedThumbnailDataUrl: string | null = null;
        const pendingSceneId = idbPendingRecording.sceneId;
        if (useCompositorForPreview && compositorRef.current && projectId && pendingSceneId) {
          const container = compositorRef.current.getContainerNode();
          if (container) {
            try {
              const blob = await captureCompositorThumbnail(container);
              if (blob) {
                capturedThumbnailDataUrl = await blobToDataUrl(blob);
                uploadCompositorThumbnail(blob, projectId, pendingSceneId).catch(() => {});
              }
            } catch (err) {
              logger.warn('[RecordingPreviewModal] Thumbnail capture failed (non-blocking):', err);
            }
          }
        }
        
        const recoveredData = {
          sceneId: idbPendingRecording.sceneId,
          duration: idbPendingRecording.duration,
          layout: idbPendingRecording.layout,
          micEnabled: idbPendingRecording.micEnabled,
          webcamSegments: idbPendingRecording.webcamSegments,
          screenSegments: idbPendingRecording.screenSegments,
          audioSegments: idbPendingRecording.audioSegments,
        };
        await uploadFromRecoveredData(recoveredData);
        setUploadStatus('upload_success');
        
        if (pendingSceneId) {
          let freshMetadata: any = null;
          if (isCloudProject && projectId) {
            try {
              const { projectsManager } = await import('../../services/projectsManager');
              const freshProjectData: any = await projectsManager.getProject(Number(projectId));
              const freshScene = freshProjectData.scenes?.find((s: any) => s.id === Number(pendingSceneId));
              if (freshScene) freshMetadata = freshScene.metadata;
            } catch {}
          }
          setSceneList((prevList) => {
            const updatedList = prevList.map((s) =>
              String(s.id) === String(pendingSceneId)
                ? { 
                    ...s, 
                    status: 'recorded' as const, 
                    duration: idbPendingRecording.duration,
                    recordedAt: Date.now(),
                    ...(capturedThumbnailDataUrl ? { thumbnail: capturedThumbnailDataUrl } : {}),
                    ...(freshMetadata ? { metadata: freshMetadata } : {}),
                  }
                : s
            );
            const updatedCurrentScene = updatedList.find(s => String(s.id) === String(pendingSceneId));
            if (updatedCurrentScene && String(currentScene?.id) === String(pendingSceneId)) {
              setCurrentScene(updatedCurrentScene);
            }
            return updatedList;
          });
          logger.info('[RecordingPreviewModal] Updated local scene list status to recorded', { sceneId: pendingSceneId, hasThumbnail: !!capturedThumbnailDataUrl });
        }
        
        setIdbPendingRecording(null);
        hidePreview();
        
        logger.info('[RecordingPreviewModal] IndexedDB pending recording uploaded successfully');
      } catch (error) {
        logger.error('[RecordingPreviewModal] IndexedDB pending upload failed', error);
        setUploadStatus('upload_failed');
        setIsConfirming(false);
      }
      return;
    }
    
    // Handle separate stream recordings (deferred upload - in-memory)
    if (separateStreamPendingRecording) {
      logger.info('[RecordingPreviewModal] Uploading separate stream recording', {
        sceneId: separateStreamPendingRecording.sceneId,
        duration: separateStreamPendingRecording.duration,
      });
      
      try {
        setUploadStatus('uploading');
        
        // THUMBNAIL: Capture BEFORE upload/status change while compositor is still mounted
        let capturedThumbnailDataUrl: string | null = null;
        const capturedSceneId = separateStreamPendingRecording.sceneId;
        if (useCompositorForPreview && compositorRef.current && projectId && capturedSceneId) {
          const container = compositorRef.current.getContainerNode();
          if (container) {
            try {
              const blob = await captureCompositorThumbnail(container);
              if (blob) {
                capturedThumbnailDataUrl = await blobToDataUrl(blob);
                uploadCompositorThumbnail(blob, projectId, capturedSceneId).catch(() => {});
              }
            } catch (err) {
              logger.warn('[RecordingPreviewModal] Thumbnail capture failed (non-blocking):', err);
            }
          }
        }
        
        const uploadResult = await uploadSeparateStreamRecording();
        setUploadStatus('upload_success');
        
        // Update local scene list to reflect 'recorded' status, metadata, AND thumbnail
        const pendingSceneId = separateStreamPendingRecording.sceneId;
        if (pendingSceneId) {
          const updatedSceneList = sceneList.map((s) => {
            if (String(s.id) !== String(pendingSceneId)) return s;
            
            const existingMetadata = typeof s.metadata === 'string' 
              ? JSON.parse(s.metadata) 
              : (s.metadata || {});
            const newMetadata = uploadResult?.metadata 
              ? { ...existingMetadata, ...uploadResult.metadata }
              : existingMetadata;
            
            return { 
              ...s, 
              status: 'recorded' as const, 
              duration: separateStreamPendingRecording.duration,
              recordedAt: Date.now(),
              metadata: newMetadata,
              videoUrl: uploadResult?.videoUrl || s.videoUrl,
              ...(capturedThumbnailDataUrl ? { thumbnail: capturedThumbnailDataUrl } : {}),
            };
          });
          
          flushSync(() => {
            setSceneList(updatedSceneList);
            if (activeProject) {
              setActiveProject({
                ...activeProject,
                scenes: updatedSceneList
              });
            }
            const updatedCurrentScene = updatedSceneList.find(s => String(s.id) === String(pendingSceneId));
            if (updatedCurrentScene && String(currentScene?.id) === String(pendingSceneId)) {
              setCurrentScene(updatedCurrentScene);
            }
          });
          logger.info('[RecordingPreviewModal] Updated local scene list with recorded status, metadata, and thumbnail', { 
            sceneId: pendingSceneId,
            hasMetadata: !!uploadResult?.metadata,
            hasThumbnail: !!capturedThumbnailDataUrl,
          });
        }
        
        hidePreview();
        
        logger.info('[RecordingPreviewModal] Separate stream recording uploaded successfully');
      } catch (error) {
        logger.error('[RecordingPreviewModal] Separate stream upload failed', error);
        setUploadStatus('upload_failed');
        setIsConfirming(false);
      }
      return;
    }
    
    // Support both 'scene' mode AND 'single' mode when currentScene is available
    // This handles the case where recording opens in 'single' mode but we have a currentScene
    const effectiveSceneId = sceneId || currentScene?.id;
    const effectiveScene = scene || currentScene;
    
    // DURATION LIMIT CHECK: Verify total project duration doesn't exceed user's tier limit
    // This prevents users from confirming recordings that would push them over their limit
    const currentRecordingDurationMs = videoDuration > 0 
      ? Math.round(videoDuration * 1000) 
      : (duration || effectiveScene?.duration || 0);
    
    // Calculate total duration of already-recorded scenes (excluding current scene being confirmed)
    const otherScenesRecordedDurationMs = sceneList
      .filter(s => s.id !== effectiveSceneId && s.status === 'recorded' && s.duration)
      .reduce((total, s) => total + (s.duration || 0), 0);
    
    const totalProjectDurationMs = otherScenesRecordedDurationMs + currentRecordingDurationMs;
    const totalProjectDurationSeconds = totalProjectDurationMs / 1000;
    
    // Get user's duration limit (in minutes, null means unlimited)
    const durationLimitMinutes = getLimit('DURATION_PER_VIDEO');
    const durationLimitSeconds = durationLimitMinutes !== null ? durationLimitMinutes * 60 : null;
    
    // Add grace period (2 seconds) to account for auto-stop timing delays
    // Recording may exceed limit by 1-2 seconds due to stop timing, so we allow small buffer
    const DURATION_GRACE_PERIOD_SECONDS = 2;
    const effectiveLimitSeconds = durationLimitSeconds !== null 
      ? durationLimitSeconds + DURATION_GRACE_PERIOD_SECONDS 
      : null;
    
    // Check if over limit (only for non-paid users with a defined limit)
    if (!isPaid && effectiveLimitSeconds !== null && totalProjectDurationSeconds > effectiveLimitSeconds) {
      logger.info('[RecordingPreviewModal] Total project duration exceeds limit', {
        totalProjectDurationSeconds,
        durationLimitSeconds,
        currentRecordingDurationMs,
        otherScenesRecordedDurationMs,
      });
      
      setIsConfirming(false);
      showUpgradeModal({
        feature: 'Extended Recording',
        description: `Your project total duration (${Math.floor(totalProjectDurationSeconds / 60)}:${String(Math.floor(totalProjectDurationSeconds % 60)).padStart(2, '0')}) exceeds your ${durationLimitMinutes}-minute limit. Upgrade to record longer videos.`,
        source: 'recording_duration_limit',
      });
      return;
    }
    
    // Handle pending_confirmation scenes (already uploaded, just need status update)
    // These have s3Key (videoUrl) - videoGuid may still be set from recording session
    // Key indicator: status is 'pending_confirmation' AND s3Key exists (video already in S3)
    // Note: s3Key might be in s3KeyStorage if upload completed after modal opened
    const effectiveS3Key = s3Key || (videoGuid ? s3KeyStorage.get(videoGuid) : null);
    const isPendingConfirmation = effectiveScene?.status === 'pending_confirmation' && effectiveS3Key;
    
    if (effectiveSceneId && effectiveScene && (videoGuid || isPendingConfirmation)) {
      try {
        // For pending_confirmation scenes, skip upload - video is already in S3
        if (isPendingConfirmation) {
          // Preserve existing thumbnail URL
          let existingThumbnailUrl = effectiveScene.thumbnailUrl;
          
          // Priority for duration:
          // 1. Video element metadata (videoDuration) - most accurate, from actual loaded video
          // 2. Duration prop (from recording timestamps)
          // 3. Existing scene duration (may be incorrect default)
          const videoDurationMs = videoDuration > 0 ? Math.round(videoDuration * 1000) : 0;
          const durationMs = videoDurationMs > 0 
            ? videoDurationMs 
            : (duration && duration > 0 ? duration : (effectiveScene.duration || 0));
          
          logger.info('[RecordingPreviewModal] Confirming pending_confirmation scene (already uploaded)', {
            sceneId: effectiveSceneId,
            sceneTitle: effectiveScene.title,
            existingVideoUrl: s3Key,
            videoDurationFromElement: videoDurationMs,
            finalDurationMs: durationMs,
            hasThumbnail: !!existingThumbnailUrl,
          });
          
          // Generate thumbnail if it doesn't exist - also extracts accurate video duration
          const isCloudProject = projectId && !isNaN(Number(projectId));
          let serverDurationMs: number | null = null;
          if (!existingThumbnailUrl && isCloudProject) {
            try {
              logger.info('[RecordingPreviewModal] Generating thumbnail for scene without one...');
              const { crudUtil } = await import('../../services/axiosCRUD');
              const response = await crudUtil.post(`/api/scene/${effectiveSceneId}/generate-thumbnail`, {
                projectId: Number(projectId)
              });
              
              if (response.thumbnailUrl) {
                existingThumbnailUrl = response.thumbnailUrl;
                logger.info('[RecordingPreviewModal] ✅ Thumbnail generated successfully', {
                  thumbnailUrl: response.thumbnailUrl.substring(0, 60) + '...',
                  existed: response.existed
                });
              }
              
              // Use server-extracted duration (from FFmpeg, more accurate for WebM files)
              if (response.durationMs && response.durationMs > 0) {
                serverDurationMs = response.durationMs;
                logger.info('[RecordingPreviewModal] ✅ Got accurate duration from server:', serverDurationMs);
              }
            } catch (thumbnailError) {
              logger.warn('[RecordingPreviewModal] Failed to generate thumbnail (non-critical):', thumbnailError);
            }
          }
          
          // Use server duration if available (most accurate), otherwise fallback to video element duration
          const finalDurationMs = serverDurationMs || durationMs;
          
          const micEnabledValue = micEnabled ?? effectiveScene.micEnabled ?? false;
          
          // Just update the status to 'recorded' - video is already in S3
          // Preserve existing videoUrl and thumbnailUrl
          const updateData = { 
            status: 'recorded' as const,
            duration: finalDurationMs,
            recordedAt: effectiveScene.recordedAt || Date.now(),
            micEnabled: micEnabledValue,
          };
          
          await SceneService.updateScene(effectiveSceneId, updateData, projectId);
          
          // Update project total duration if in a project
          if (projectId) {
            await SceneService.updateSceneDuration(projectId, effectiveSceneId, finalDurationMs);
          }
          
          // Update local state
          if (isCloudProject) {
            try {
              const { projectsManager } = await import('../../services/projectsManager');
              const freshProjectData: any = await projectsManager.getProject(Number(projectId));
              const freshScene = freshProjectData.scenes?.find((s: any) => s.id === Number(effectiveSceneId));
              
              if (freshScene) {
                const sceneIdStr = String(effectiveSceneId);
                const updatedSceneList = sceneList.map(s => 
                  String(s.id) === sceneIdStr 
                    ? {
                        ...s,
                        status: 'recorded' as const,
                        duration: freshScene.duration || finalDurationMs,
                        recordedAt: freshScene.recordedAt || Date.now(),
                        videoUrl: freshScene.videoUrl || s.videoUrl || s3Key,
                        thumbnailUrl: freshScene.thumbnailUrl || existingThumbnailUrl || s.thumbnailUrl,
                        micEnabled: freshScene.micEnabled ?? s.micEnabled ?? micEnabledValue,
                        metadata: freshScene.metadata || s.metadata,
                      }
                    : s
                );
                
                flushSync(() => {
                  setSceneList(updatedSceneList);
                  setActiveProject({
                    ...activeProject,
                    scenes: updatedSceneList
                  });
                  
                  const updatedCurrentScene = updatedSceneList.find(s => String(s.id) === sceneIdStr);
                  if (currentScene && String(currentScene.id) === sceneIdStr && updatedCurrentScene) {
                    setCurrentScene(updatedCurrentScene);
                  }
                });
              }
            } catch (error) {
              logger.error('[RecordingPreviewModal] Failed to refresh cloud scene data:', error);
            }
          }
          
          logger.info('[RecordingPreviewModal] pending_confirmation scene confirmed successfully');
          setIsConfirming(false);
          hidePreview();
          return;
        }
        
        // Phase 1.3: Deferred upload - trigger upload for cloud projects BEFORE saving
        // Check if this is a cloud project (has numeric project ID) regardless of mode
        // This handles both 'scene' mode AND 'single' mode when currentScene exists
        let uploadedVideoUrl: string | undefined = undefined;
        let uploadedThumbnailUrl: string | undefined = undefined;
        
        const isCloudProject = projectId && !isNaN(Number(projectId));
        if (effectiveSceneId && isCloudProject) {
          logger.info('[RecordingPreviewModal] Cloud project detected - triggering deferred upload before saving', {
            mode,
            projectId,
            sceneId: effectiveSceneId
          });
          
          const uploadResult = await triggerDeferredUpload(
            videoGuid!,
            effectiveSceneId,
            projectId
          );

          if (!uploadResult.success) {
            // Provide user-friendly error messages for specific cases
            let errorMessage = uploadResult.error || 'Unknown error';
            if (errorMessage.includes('No chunks found')) {
              errorMessage = 'Recording data was lost. This can happen if browser storage was cleared. Please re-record this scene.';
            }
            setError(`Upload failed: ${errorMessage}`);
            logger.error('[RecordingPreviewModal] Deferred upload failed, not saving scene');
            setIsConfirming(false);
            return;
          }

          // Capture the uploaded URLs to use directly when saving scene
          uploadedVideoUrl = uploadResult.fileUrl;
          uploadedThumbnailUrl = uploadResult.thumbnailUrl;

          logger.info('[RecordingPreviewModal] Deferred upload successful, proceeding to save scene', {
            hasFileUrl: !!uploadedVideoUrl,
            hasThumbnailUrl: !!uploadedThumbnailUrl,
            videoUrl: uploadedVideoUrl ? uploadedVideoUrl.substring(0, 60) + '...' : 'N/A',
          });
        }
        // Delete old recording chunks if this is a re-record
        // (Old chunks have different videoGuid than current recording)
        const existingChunks = await db.chunks.where('sceneId').equals(effectiveSceneId).toArray();
        const oldChunks = existingChunks.filter(chunk => chunk.videoGuid !== videoGuid);
        
        if (oldChunks.length > 0) {
          const oldVideoGuids = Array.from(new Set(oldChunks.map(c => c.videoGuid)));
          
          // Delete old chunks from IndexedDB
          await Promise.all(oldVideoGuids.map(oldGuid => 
            db.chunks.where('videoGuid').equals(oldGuid).delete()
          ));
          
          logger.info('[RecordingPreviewModal] Deleted old recording chunks before saving new one');
        }
        
        // Save scene with duration (in milliseconds)
        // Use passed duration prop (from recording timestamps) - already in milliseconds
        // Fallback to videoDuration only if duration prop is missing/zero
        const durationMs = duration && duration > 0 
          ? duration 
          : Math.round(videoDuration * 1000);
        
        // Use uploaded URLs directly from deferred upload result
        const videoUrl = uploadedVideoUrl; // Use URL from upload result
        const thumbnailUrl = uploadedThumbnailUrl; // Use thumbnail from upload result
        
        logger.info('[RecordingPreviewModal] Accepting scene with duration:', {
          sceneId: effectiveSceneId,
          sceneTitle: effectiveScene.title,
          durationSeconds: videoDuration,
          durationMs,
          hasVideoUrl: !!videoUrl,
          hasThumbnailUrl: !!thumbnailUrl,
          videoUrl: videoUrl ? videoUrl.substring(0, 60) + '...' : 'N/A'
        });
        
        const micEnabledValue = micEnabled ?? effectiveScene.micEnabled ?? false;
        
        const updateData = { 
          status: 'recorded' as const,
          duration: durationMs,
          recordedAt: Date.now(),
          micEnabled: micEnabledValue, // Save whether microphone was enabled during recording
          ...(videoUrl && { videoUrl }), // Include videoUrl only if it exists
          ...(thumbnailUrl ? { thumbnailUrl } : {}) // Include thumbnailUrl if generated
        };
        
        await SceneService.updateScene(effectiveSceneId, updateData, projectId);
        
        // Update project total duration if in a project
        if (projectId) {
          await SceneService.updateSceneDuration(projectId, effectiveSceneId, durationMs);
        }
        
        // For cloud projects, fetch fresh scene data from PostgreSQL to update UI
        if (isCloudProject) {
          try {
            const { projectsManager } = await import('../../services/projectsManager');
            const freshProjectData: any = await projectsManager.getProject(Number(projectId));
            const freshScene = freshProjectData.scenes?.find((s: any) => s.id === Number(effectiveSceneId));
            
            if (freshScene) {
              // Update scene list with fresh data from cloud
              // Convert both IDs to strings for comparison (cloud IDs are numbers, local IDs might be strings)
              const sceneIdStr = String(effectiveSceneId);
              
              // Check if scene has recording - either via videoUrl OR separate streams metadata
              const hasSeparateStreamsRecording = freshScene.metadata?.streams && (
                (freshScene.metadata.streams.screen?.segments?.length > 0 && freshScene.metadata.streams.screen.segments[0].key) ||
                (freshScene.metadata.streams.webcam?.segments?.length > 0 && freshScene.metadata.streams.webcam.segments[0].key)
              );
              const hasRecording = freshScene.videoUrl || hasSeparateStreamsRecording;
              
              const updatedSceneList = sceneList.map(s => 
                String(s.id) === sceneIdStr 
                  ? {
                      ...s,
                      status: hasRecording ? 'recorded' as const : 'pending' as const,
                      duration: freshScene.duration || durationMs,
                      recordedAt: Date.now(),
                      videoUrl: freshScene.videoUrl || s.videoUrl,
                      thumbnailUrl: freshScene.thumbnailUrl || s.thumbnailUrl,
                      micEnabled: freshScene.micEnabled ?? s.micEnabled ?? micEnabledValue,
                      metadata: freshScene.metadata || s.metadata,
                    }
                  : s
              );
              
              // Use flushSync to force synchronous rendering of atom updates
              // This ensures all components (including SceneTimelineEditor) re-render
              // BEFORE we close the modal, preventing React batching from delaying updates
              flushSync(() => {
                setSceneList(updatedSceneList);
                setActiveProject({
                  ...activeProject,
                  scenes: updatedSceneList
                });
                
                // CRITICAL: Update currentScene if it's the scene we just updated
                // This ensures the "Retake" button shows correctly
                const updatedCurrentScene = updatedSceneList.find(s => String(s.id) === sceneIdStr);
                if (currentScene && String(currentScene.id) === sceneIdStr && updatedCurrentScene) {
                  setCurrentScene(updatedCurrentScene);
                }
                
                // CRITICAL: Update videoDuration state so UI shows correct duration
                const freshDurationSeconds = (freshScene.duration || durationMs) / 1000;
                setVideoDuration(freshDurationSeconds);
              });
              
              // Give a brief moment for any async rendering to complete
              await new Promise(resolve => setTimeout(resolve, 50));
            } else {
              // Use flushSync for consistency
              const updatedList = sceneList.map(s => 
                s.id === effectiveSceneId 
                  ? { ...s, status: 'recorded' as const, recordedAt: Date.now(), duration: durationMs }
                  : s
              );
              flushSync(() => {
                setSceneList(updatedList);
                if (activeProject) {
                  setActiveProject({ ...activeProject, scenes: updatedList });
                }
                
                // CRITICAL: Update currentScene if it's the scene we just updated
                const updatedCurrentScene = updatedList.find(s => s.id === effectiveSceneId);
                if (currentScene && currentScene.id === effectiveSceneId && updatedCurrentScene) {
                  setCurrentScene(updatedCurrentScene);
                }
              });
            }
          } catch (error) {
            logger.error('[RecordingPreviewModal] Failed to refresh cloud scene data:', error);
            // Fallback to local update with flushSync
            const updatedList = sceneList.map(s => 
              s.id === effectiveSceneId 
                ? { ...s, status: 'recorded' as const, recordedAt: Date.now(), duration: durationMs }
                : s
            );
            flushSync(() => {
              setSceneList(updatedList);
              if (activeProject) {
                setActiveProject({ ...activeProject, scenes: updatedList });
              }
              
              // CRITICAL: Update currentScene if it's the scene we just updated
              const updatedCurrentScene = updatedList.find(s => s.id === effectiveSceneId);
              if (currentScene && currentScene.id === effectiveSceneId && updatedCurrentScene) {
                setCurrentScene(updatedCurrentScene);
              }
            });
          }
        } else {
          // For local projects, use the same flushSync pattern for consistency
          const updatedList = sceneList.map(s => 
            s.id === effectiveSceneId 
              ? { ...s, status: 'recorded' as const, recordedAt: Date.now(), duration: durationMs }
              : s
          );
          flushSync(() => {
            setSceneList(updatedList);
            if (activeProject) {
              setActiveProject({ ...activeProject, scenes: updatedList });
            }
            
            // CRITICAL: Update currentScene if it's the scene we just updated
            const updatedCurrentScene = updatedList.find(s => s.id === effectiveSceneId);
            if (currentScene && currentScene.id === effectiveSceneId && updatedCurrentScene) {
              setCurrentScene(updatedCurrentScene);
            }
          });
        }
        
        logger.info('[RecordingPreviewModal] Scene accepted successfully');
        setIsConfirming(false);
        
        // Phase 5: Safe chunk cleanup - ONLY delete chunks after upload_success is verified
        // This ensures we never lose data if upload fails or scene save fails
        if (uploadStatus === 'upload_success' && videoGuid) {
          try {
            // Delete chunks for this successful upload
            await db.chunks.where('videoGuid').equals(videoGuid).delete();
            
            // Clean up S3 key from temporary storage
            s3KeyStorage.delete(videoGuid);
            
            logger.info('[RecordingPreviewModal] Chunks deleted after verified upload success');
          } catch (cleanupError) {
            // Non-critical error - chunks remain but scene is saved
            logger.warn('[RecordingPreviewModal] Failed to cleanup chunks (non-critical):', cleanupError);
          }
        } else if (mode === 'scene') {
          // In scene mode, if upload didn't happen or failed, chunks are preserved
          logger.warn('[RecordingPreviewModal] Chunks NOT deleted - upload not verified', {
            uploadStatus,
            videoGuid: videoGuid ? videoGuid.substring(0, 20) + '...' : 'NULL'
          });
        }
        
        handleClose();
        
        // Move to next scene if available
        const currentIndex = sceneList.findIndex(s => s.id === effectiveSceneId);
        if (currentIndex < sceneList.length - 1) {
          goToNextScene();
        }
      } catch (error) {
        logger.error('[RecordingPreviewModal] Failed to accept scene:', error);
        setError('Failed to save scene. Please try again.');
        setIsConfirming(false); // Reset confirming state on error
      }
    } else {
      // No valid scene to confirm, reset state
      setIsConfirming(false);
    }
  }, [mode, sceneId, scene, currentScene, videoGuid, s3Key, micEnabled, duration, videoDuration, projectId, activeProject, sceneList, goToNextScene, handleClose, hidePreview, setActiveProject, setSceneList, setCurrentScene, triggerDeferredUpload, uploadStatus, isConfirming, getLimit, isPaid, showUpgradeModal]);

  const handleReRecordScene = useCallback(async () => {
    if (mode === 'scene' && sceneId && onReRecord) {
      Modal.confirm({
        title: 'Re-record Scene?',
        content: 'The current recording will be kept until you accept the new recording.',
        okText: 'Re-record',
        okType: 'primary',
        cancelText: 'Cancel',
        okButtonProps: {
          style: { background: '#4A3CFA' }
        },
        onOk: async () => {
          try {
            // Clean up any temporary analysis audio that may have been uploaded
            const numericSceneId = parseInt(sceneId);
            if (!isNaN(numericSceneId)) {
              SceneAnalysisService.cleanupTempAudio(numericSceneId).catch(err => {
                console.warn('[RecordingPreviewModal] Failed to cleanup temp analysis audio:', err);
              });
            }
            
            // DON'T delete old chunks/video/thumbnail yet!
            // They will be deleted when "Looks Good" is clicked on the new recording
            // Just mark scene as pending so it can be recorded again
            await SceneService.updateSceneStatus(sceneId, 'pending', projectId);
            
            // CRITICAL: Refresh scene list from PostgreSQL to preserve thumbnailUrl in frontend state
            const isCloudProjectReRecord = projectId && !isNaN(Number(projectId));
            if (isCloudProjectReRecord) {
              const { projectsManager } = await import('../../services/projectsManager');
              const freshProject: any = await projectsManager.getProject(Number(projectId));
              
              // Update scene list with fresh data that includes preserved thumbnailUrl
              if (freshProject.scenes) {
                const updatedSceneList = sceneList.map(s => {
                  const freshScene = freshProject.scenes.find((fs: any) => fs.id === Number(s.id));
                  return freshScene ? {
                    ...s,
                    status: freshScene.status as SceneStatus,
                    videoUrl: freshScene.videoUrl,
                    thumbnailUrl: freshScene.thumbnailUrl,  // PRESERVE thumbnail!
                    micEnabled: freshScene.micEnabled ?? s.micEnabled  // PRESERVE micEnabled!
                  } : s;
                });
                
                flushSync(() => {
                  setSceneList(updatedSceneList);
                  setActiveProject({ ...activeProject, scenes: updatedSceneList });
                });
                
                logger.info('[RecordingPreviewModal] Scene list refreshed - thumbnail preserved for re-record');
              }
            }
            
            logger.info('[RecordingPreviewModal] Scene marked for re-recording (old recording preserved)');
            handleClose();
            onReRecord(sceneId);
          } catch (error) {
            logger.error('[RecordingPreviewModal] Failed to mark scene for re-recording:', error);
            setError('Failed to prepare scene for re-recording. Please try again.');
          }
        },
      });
    }
  }, [mode, sceneId, onReRecord, handleClose, activeProject, sceneList, setSceneList, setActiveProject]);

  const handleSaveSingleRecording = useCallback(async () => {
    logger.info('[RecordingPreviewModal] Save clicked', { 
      mode, 
      videoGuid, 
      hasActiveProject: !!activeProject,
      projectId: activeProject?.id,
      projectTitle: activeProject?.title 
    });
    
    if (mode !== 'single') {
      logger.error('[RecordingPreviewModal] Invalid mode for single recording save:', mode);
      setError('Invalid recording mode. Please try recording again.');
      return;
    }
    
    if (!videoGuid) {
      logger.error('[RecordingPreviewModal] No videoGuid available for save');
      setError('No video data found. Please try recording again.');
      return;
    }
    
    if (!activeProject) {
      logger.error('[RecordingPreviewModal] No active project found - this should not happen');
      setError('No active project found. Please select a template or create a project first.');
      return;
    }

    try {
      // Get all chunks for this videoGuid
      const chunks = await db.chunks.where('videoGuid').equals(videoGuid).toArray();
      const chunkIds = chunks.map(chunk => chunk.id).sort();
      
      // Create a new scene for the single recording with actual duration
      // Use passed duration prop (from recording timestamps) - already in milliseconds
      const durationMs = duration && duration > 0 
        ? duration 
        : Math.round(videoDuration * 1000);

      // Get video URL and thumbnail from storage
      const s3Key = s3KeyStorage.get(videoGuid);
      const thumbnailUrl = thumbnailStorage.get(videoGuid);
      
      const newScene: Scene = {
        id: `scene-${Date.now()}`,
        order: sceneList.length,
        title: `Recording ${sceneList.length + 1}`,
        script: '',
        layout: [],
        background: { type: 'solid', colors: ['#667eea'] },
        status: 'recorded',
        duration: durationMs,
        suggestedDuration: durationMs,
        recordedAt: Date.now(),
        videoChunks: chunkIds,
        videoUrl: s3Key || undefined,
        thumbnailUrl: thumbnailUrl || undefined,
      };

      logger.info('[RecordingPreviewModal] Adding scene to project:', { 
        projectId: activeProject.id, 
        sceneId: newScene.id,
        sceneTitle: newScene.title,
        hasVideoUrl: !!newScene.videoUrl,
        hasThumbnailUrl: !!newScene.thumbnailUrl
      });
      
      await SceneService.addSceneToProject(activeProject.id, newScene);
      
      logger.info('[RecordingPreviewModal] Single recording saved as scene successfully');
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('[RecordingPreviewModal] Failed to save recording:', { 
        error: errorMessage, 
        projectId: activeProject.id,
        stack: error instanceof Error ? error.stack : undefined 
      });
      
      // Provide helpful error messages based on the error type
      if (errorMessage.includes('Project not found')) {
        setError('Project database issue detected. The project was auto-created. Please try saving again.');
      } else if (errorMessage.includes('Scene not found')) {
        setError('Scene creation failed. Please try recording again.');
      } else {
        setError(`Failed to save recording. ${errorMessage}. Please try again or contact support if the issue persists.`);
      }
    }
  }, [mode, videoGuid, activeProject, duration, videoDuration, sceneList, handleClose]);

  const handleDeleteAndStartOver = useCallback(async () => {
    const effectiveSceneId = sceneId || currentScene?.id;
    const effectiveScene = scene || currentScene;
    
    Modal.confirm({
      title: 'Delete & Start Over?',
      content: 'This will permanently delete this recording and you can record again. Are you sure?',
      okText: 'Delete & Start Over',
      okType: 'danger',
      cancelText: 'Cancel',
      okButtonProps: {
        danger: true
      },
      onOk: async () => {
        try {
          // Clean up any temporary analysis audio that may have been uploaded
          if (effectiveSceneId) {
            const numericSceneId = parseInt(effectiveSceneId);
            if (!isNaN(numericSceneId)) {
              SceneAnalysisService.cleanupTempAudio(numericSceneId).catch(err => {
                console.warn('[RecordingPreviewModal] Failed to cleanup temp analysis audio:', err);
              });
            }
          }
          
          // Delete chunks for this recording
          if (videoGuid) {
            await db.chunks.where('videoGuid').equals(videoGuid).delete();
            logger.info('[RecordingPreviewModal] Deleted recording chunks', { videoGuid });
          }
          
          // If we have a scene, mark it as pending for re-recording
          if (effectiveSceneId && effectiveScene) {
            // Reset scene with analysisStatus to clear old analysis data
            await SceneService.updateScene(effectiveSceneId, {
              status: 'pending',
              analysisStatus: 'pending',
            }, activeProject?.id);
            
            // Update local scene list
            const updatedList = sceneList.map(s => 
              s.id === effectiveSceneId 
                ? { ...s, status: 'pending' as const, videoUrl: undefined, thumbnailUrl: s.thumbnailUrl, analysisStatus: 'pending' }
                : s
            );
            flushSync(() => {
              setSceneList(updatedList);
              if (activeProject) {
                setActiveProject({ ...activeProject, scenes: updatedList });
              }
            });
            
            logger.info('[RecordingPreviewModal] Scene marked as pending for re-recording');
          }
          
          handleClose();
          
          // Trigger re-record callback if provided
          if (onReRecord && effectiveSceneId) {
            onReRecord(effectiveSceneId);
          }
          
          // Trigger delete callback if provided
          if (onDelete) {
            onDelete();
          }
        } catch (error) {
          logger.error('[RecordingPreviewModal] Failed to delete recording:', error);
          setError('Failed to delete recording. Please try again.');
        }
      },
    });
  }, [sceneId, currentScene, scene, videoGuid, activeProject, sceneList, handleClose, onReRecord, onDelete, setSceneList, setActiveProject]);

  useEffect(() => {
    if (!visible) return;

    // Add a small delay before enabling keyboard shortcuts to prevent accidental triggers
    // This prevents stray Enter key presses from the recording stop action
    let keyboardEnabled = false;
    const enableTimeout = setTimeout(() => {
      keyboardEnabled = true;
    }, 500); // 500ms delay before keyboard shortcuts become active

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore keyboard events until delay has passed
      if (!keyboardEnabled) {
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        
        // Only allow Enter if upload is complete
        if (!uploadComplete) {
          return;
        }
        
        if (mode === 'scene') {
          handleAcceptScene();
        } else {
          handleSaveSingleRecording();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        
        // Only allow Esc if upload is complete
        if (!uploadComplete) {
          return;
        }
        
        handleDeleteAndStartOver();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(enableTimeout);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, mode, uploadComplete, handleAcceptScene, handleSaveSingleRecording, handleDeleteAndStartOver]);

  const formatDuration = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const togglePlayPause = () => {
    if (compositorRef.current) {
      compositorRef.current.toggle();
    } else if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const getTitle = () => {
    if (mode === 'scene' && scene && sceneId) {
      const currentIndex = sceneList.findIndex(s => s.id === sceneId);
      const sceneNumber = currentIndex >= 0 ? currentIndex + 1 : 1;
      const totalScenes = sceneList.length;
      return `Preview: Scene ${sceneNumber} of ${totalScenes}`;
    }
    return 'Preview Recording';
  };

  const getSceneMetadata = () => {
    if (mode === 'scene' && scene) {
      const layoutType = scene.layout?.length > 0 ? 'Picture-in-Picture' : 'Full Screen';
      return `Scene ${sceneList.findIndex(s => s.id === sceneId) + 1} • ${layoutType}`;
    }
    return null;
  };

  const getFooterButtons = () => {
    // Phase 1.3 & 3.1: Button states based on upload status
    const isUploading = uploadStatus === 'uploading';
    const isRetrying = uploadStatus === 'retrying';
    const uploadFailed = uploadStatus === 'upload_failed';
    const uploadSuccess = uploadStatus === 'upload_success';

    // Determine button text and state
    let buttonText = 'Looks Good';
    let buttonDisabled = !uploadComplete;
    let buttonLoading = !uploadComplete && uploadStatus === 'not_started';

    if (isConfirming) {
      // User clicked "Looks Good" - show loading state
      buttonText = 'Confirming...';
      buttonDisabled = true;
      buttonLoading = true;
    } else if (isUploading || uploadSuccess) {
      // Keep disabled during upload AND after upload succeeds (while saving scene)
      // Modal closes automatically via handleAcceptScene - no need to re-enable button
      buttonText = uploadSuccess ? 'Saving...' : `Uploading...${uploadProgress > 0 ? ` ${uploadProgress}%` : ''}`;
      buttonDisabled = true;
      buttonLoading = true;
    } else if (isRetrying) {
      // Phase 3.1: Show retry status
      buttonText = `Retrying... (Attempt ${retryCount + 1}/10)`;
      buttonDisabled = true;
      buttonLoading = false;
    } else if (uploadFailed) {
      // Phase 3.2: Manual retry button after 10 failures
      buttonText = `Retry Upload`;
      buttonDisabled = false;
      buttonLoading = false;
    } else if (uploadComplete) {
      buttonText = 'Looks Good';
      buttonDisabled = false;
      buttonLoading = false;
    }
    
    // During analysis processing: disable buttons but keep normal labels
    if (isAnalysisProcessing && !isConfirming) {
      buttonDisabled = true;
      buttonLoading = false; // Don't show loading spinner
      buttonText = 'Looks Good'; // Keep normal label
    }

    const buttons = [];

    // Hide delete button during upload, retry, confirming, AND after upload succeeds (while saving)
    if (!isUploading && !isRetrying && !uploadSuccess && !isConfirming) {
      buttons.push(
        <Button 
          key="delete" 
          danger
          onClick={handleDeleteAndStartOver}
          disabled={!uploadComplete || isAnalysisProcessing || isConfirming}
        >
          Delete & Start Over
        </Button>
      );
    }

    buttons.push(
      <Button 
        key="accept" 
        type="primary"
        onClick={handleAcceptScene}
        disabled={buttonDisabled}
        loading={buttonLoading}
        style={isConfirming ? { color: '#fff' } : undefined}
      >
        {buttonText}
      </Button>
    );

    return buttons;
  };

  // Don't show preview modal in blank recording mode - it only shows in project mode
  if (isBlankMode) {
    return null;
  }

  const modalTitle = (
    <div className="modal-header-content">
      <div className="header-icon-badge">
        <MonitorPlay size={20} />
      </div>
      <div className="header-text">
        <h3 className="header-title">{getTitle()}</h3>
        {getSceneMetadata() && (
          <p className="header-subtitle">{getSceneMetadata()}</p>
        )}
      </div>
    </div>
  );

  const formatDurationDisplay = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={handleClose}
      afterClose={handleAfterClose}
      footer={getFooterButtons()}
      width={900}
      className="scene-preview-modal recording-preview-modal preview-modal-redesigned"
      destroyOnHidden
      maskClosable={false}
      keyboard={false}
    >
      <div className="scene-preview-content">
        {loading && (
          <div className="preview-loading-state">
            <Spin size="large" />
            <p className="loading-text">Loading preview...</p>
          </div>
        )}

        {error && (
          <div className="preview-error-state">
            <div className="error-icon">
              <AlertCircle />
            </div>
            <h4 className="error-title">Error Loading Preview</h4>
            <p className="error-description">{error}</p>
            <Button onClick={() => setError(null)} style={{ marginTop: 16 }}>Dismiss</Button>
          </div>
        )}

        {!loading && !error && (videoUrl || (useCompositorForPreview && compositorRecordingData)) && (
          <>
            <div className="preview-video-section">
              <div className="video-wrapper">
                {mode === 'scene' && scene && (
                  <div className="scene-badge">
                    <Film size={12} />
                    <span>Scene {sceneList.findIndex(s => s.id === sceneId) + 1}</span>
                  </div>
                )}
                
                {useCompositorForPreview && compositorRecordingData ? (
                  <CompositorPlayer
                    ref={compositorRef}
                    recordingData={compositorRecordingData}
                    pendingBlobUrls={compositorBlobUrls}
                    className="preview-video"
                    showControls={true}
                    autoPlay={false}
                    loop={false}
                    onReady={(manifest) => {
                      logger.info('[RecordingPreviewModal] Compositor player ready', { manifest });
                      if (manifest && manifest.durationMs > 0) {
                        setVideoDuration(manifest.durationMs / 1000);
                      } else if (duration && duration > 0) {
                        setVideoDuration(duration / 1000);
                      }
                    }}
                    onTimeUpdate={(timeMs) => {
                      setCurrentTime(timeMs / 1000);
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => {
                      setIsPlaying(false);
                      setCurrentTime(0);
                    }}
                    onError={(error) => {
                      logger.error('[RecordingPreviewModal] Compositor player error:', error);
                      setError(`Compositor error: ${error.message}`);
                    }}
                  />
                ) : (
                  <video
                    ref={videoRef}
                    src={videoUrl!}
                    className="preview-video"
                    controls
                    playsInline
                    onTimeUpdate={(e) => {
                      const time = e.currentTarget.currentTime;
                      if (time >= 0 && time < 1000000) {
                        setCurrentTime(time);
                      }
                    }}
                    onPause={() => setIsPlaying(false)}
                    onPlay={() => setIsPlaying(true)}
                    onEnded={() => {
                      setIsPlaying(false);
                      setCurrentTime(0);
                    }}
                    onLoadedMetadata={(e) => {
                      const video = e.currentTarget;
                      if (duration && duration > 0) {
                        const durationSeconds = duration / 1000;
                        setVideoDuration(durationSeconds);
                      } else {
                        if (isFinite(video.duration) && video.duration > 0) {
                          setVideoDuration(video.duration);
                        } else {
                          setVideoDuration(0);
                        }
                      }
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
                
                {videoDuration > 0 && (
                  <div className="duration-badge">
                    <Clock size={12} />
                    <span>{formatDurationDisplay(videoDuration)}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="keyboard-hints">
              <span className="hint-item">
                <kbd>Enter</kbd> Accept
              </span>
              <span className="hint-item">
                <kbd>Esc</kbd> Delete & Start Over
              </span>
            </div>
            
            {(scene || currentScene) && (
              <div className="scene-analysis-wrapper">
                <SceneAnalysis 
                  key={`analysis-${(scene || currentScene)!.id}-${(scene || currentScene)?.recordedAt || effectivePendingRecording?.duration || 0}`}
                  sceneId={parseInt((scene || currentScene)!.id)} 
                  micEnabled={micEnabled ?? (scene || currentScene)?.micEnabled ?? false}
                  videoGuid={videoGuid}
                  scene={scene || currentScene}
                  projectId={projectId}
                  hasAudioSegments={
                    // For separate stream recordings, check if any audio segments exist (don't rely on boolean flags)
                    useCompositorForPreview 
                      ? (
                          // Check in-memory pending recording
                          (((effectivePendingRecording as any)?.audioSegmentBlobUrls?.length ?? 0) > 0) ||
                          // Check IDB-recovered pending recording
                          ((idbPendingRecording?.audioSegments?.length ?? 0) > 0)
                        )
                      : undefined // For legacy recordings, let component use its own logic
                  }
                  isUnconfirmedSeparateStream={
                    // For unconfirmed separate stream recordings, audio segments need to be uploaded first
                    useCompositorForPreview && (effectivePendingRecording || idbPendingRecording) && !scene?.videoUrl
                  }
                  audioSegmentBlobs={
                    // Build audio segment blobs for unconfirmed separate stream recordings (don't rely on boolean flags)
                    (useCompositorForPreview && (effectivePendingRecording || idbPendingRecording) && !scene?.videoUrl)
                      ? (() => {
                          // Get audio segments from in-memory pending recording or IDB recovery
                          const inMemoryAudioSegments = (effectivePendingRecording as any)?.audioSegmentBlobUrls as Array<{ segmentIndex?: number; blobUrl: string }> | undefined;
                          const idbSegments = idbPendingRecording?.audioSegments;
                          
                          if (inMemoryAudioSegments && inMemoryAudioSegments.length > 0) {
                            // In-memory pending recording: use audioSegmentBlobUrls
                            return inMemoryAudioSegments.map((seg, idx) => ({
                              blob: new Blob(), // Placeholder - will be fetched from blob URL
                              segmentIndex: seg.segmentIndex ?? idx,
                              tsStart: 0,
                              tsEnd: effectivePendingRecording?.duration ?? null,
                              hasMic: true,
                              hasSystemAudio: false,
                              isMuted: false,
                              blobUrl: seg.blobUrl,
                            }));
                          } else if (idbSegments) {
                            // IDB-recovered: segments already have blobs
                            return idbSegments.map((seg) => ({
                              blob: seg.blob,
                              segmentIndex: seg.segmentIndex,
                              tsStart: seg.tsStart,
                              tsEnd: seg.tsEnd,
                              hasMic: seg.hasMic,
                              hasSystemAudio: seg.hasSystemAudio,
                              isMuted: seg.isMuted,
                            }));
                          }
                          return undefined;
                        })()
                      : undefined
                  }
                  onAnalysisStatusChange={(isProcessing) => {
                    setIsAnalysisProcessing(isProcessing);
                  }}
                  onSeekToTime={(timeInSeconds) => {
                    if (useCompositorForPreview && compositorRef.current) {
                      compositorRef.current.seekToSeconds(timeInSeconds);
                      compositorRef.current.play();
                    } else if (videoRef.current) {
                      videoRef.current.currentTime = timeInSeconds;
                      videoRef.current.play();
                    }
                  }}
                  onUploadNeeded={async (isTemp?: boolean) => {
                    const activeScene = scene || currentScene;
                    if (!activeScene?.videoUrl && videoGuid && activeScene?.id && projectId) {
                      const result = await triggerDeferredUploadAttempt(
                        videoGuid,
                        String(activeScene.id),
                        projectId,
                        micEnabled,
                        isTemp
                      );
                      
                      if (result.success && result.fileUrl) {
                        try {
                          const updatedScene = await SceneService.getSceneById(activeScene.id, String(projectId));
                          
                          if (updatedScene) {
                            const updatedSceneList = sceneList.map(s => 
                              s.id === activeScene.id ? updatedScene : s
                            );
                            setSceneList(updatedSceneList);
                            if (activeProject) {
                              setActiveProject({ ...activeProject, scenes: updatedSceneList });
                            }
                            if (currentScene && currentScene.id === activeScene.id) {
                              setCurrentScene(updatedScene);
                            }
                          }
                          
                          return updatedScene?.videoUrl || result.fileUrl;
                        } catch (err) {
                          console.error('[RecordingPreviewModal] Failed to fetch updated scene:', err);
                          return result.fileUrl;
                        }
                      }
                      
                      return result.fileUrl || null;
                    }
                    return activeScene?.videoUrl || null;
                  }}
                />
              </div>
            )}
          </>
        )}

        {!loading && !error && !videoUrl && !useCompositorForPreview && (
          <div className="preview-empty-state">
            <div className="empty-icon">
              <Video />
            </div>
            <h4 className="empty-title">No Video Available</h4>
            <p className="empty-description">The recording could not be loaded.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RecordingPreviewModal;
